import React, { useState, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WorkflowTemplatePanel from '../Panel/WorkflowTemplatePanel';
import WorkflowPropertiesContainer from '../ItemProperties/WorkflowPropertiesContainer';
import './RightPanel.css';
import VisualWorkflowTreePanel from '../Panel/VisualWorkflowTreePanel';

const DRAWER_WIDTH = 400;

const RightPanel = ({ open, onToggle }) => {
  const [topHeight, setTopHeight] = useState(50); // Percentage
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = React.useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !panelRef.current) return;
    
    const panelRect = panelRef.current.getBoundingClientRect();
    const newTopHeight = ((e.clientY - panelRect.top) / panelRect.height) * 100;
    
    // Constrain between 20% and 80%
    if (newTopHeight >= 20 && newTopHeight <= 80) {
      setTopHeight(newTopHeight);
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  return (
    <>
      {/* Hamburger Icon - Absolute positioned at top-right */}
      <IconButton
        onClick={onToggle}
        className={`right-panel-hamburger ${open ? 'open' : ''}`}
        aria-label={open ? "Close panel" : "Open panel"}
        title={open ? "Close panel" : "Open workflow panel"}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </IconButton>

      {/* Sliding Panel - Custom positioned */}
      <Box
        ref={panelRef}
        className={`right-panel-drawer ${open ? 'open' : ''}`}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: DRAWER_WIDTH,
          height: '100%',
          backgroundColor: '#fff',
          boxShadow: open ? '-2px 0 8px rgba(0, 0, 0, 0.15)' : 'none',
          transform: open ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Top Section - Workflow Tree */}
        <Box
          id="visualWorkflow"
          sx={{
            height: `${topHeight}%`,
            overflow: 'auto',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: 'white',
          }}
        >
          <VisualWorkflowTreePanel targetId={"visualWorkflow"} />
        </Box>

        {/* Resizable Divider */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            height: '8px',
            backgroundColor: '#e0e0e0',
            cursor: 'row-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#1976d2',
            },
            userSelect: 'none',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <DragIndicatorIcon 
            sx={{ 
              fontSize: 16, 
              color: isDragging ? '#1976d2' : '#999',
              transform: 'rotate(90deg)',
            }} 
          />
        </Box>

        {/* Bottom Section - Item Properties */}
        <Box
          sx={{
            height: `${100 - topHeight}%`,
            overflow: 'auto',
            backgroundColor: 'white',
            minHeight: 0,
          }}
        >
          <WorkflowPropertiesContainer />
        </Box>
      </Box>
    </>
  );
};

export default RightPanel;
